<template>
    <div>
        <hts-drawer
            v-model="visible"
            color="main"
            :title="$t('labels.driverModule.license.list')"
            size="large"
            @close="closeLicense">
            <div class="mb-3 text-right">
                <hts-form-button
                    v-if="checkPermission(layoutPermissions.driver_license_add)"
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    type="submit"
                    @click="openAddLicense">
                    {{ $t('labels.driverModule.license.add') }}
                </hts-form-button>
            </div>
            <hts-data-table
                ref="dataLicense"
                :headers="licenseDriver"
                :server="server"
                :detail-row-auto-height="true"
                :use-archive="useArchive"
                :use-restore="useRestore"
                use-selection
                store-name="driverDataTable"
                store-key="DriverLicense" />
            <add-licenses
                v-if="checkPermission(layoutPermissions.driver_license_add) && visible"
                ref="addLicense"
                :code-driver="idDriver"
                @refresh-tables="refreshTables('add')" />

            <edit-license
                v-if="checkPermission(layoutPermissions.driver_license_edit) && visible"
                ref="editLicense"
                :code-driver="idDriver"
                :show="showEdit"
                :license="licenseSelected"
                @refresh-tables="refreshTables('add')"
                @close="showEdit = false" />

            <hts-modal
                v-if="checkPermission(layoutPermissions.driver_license_delete)"
                :show="showDelete"
                :title="$t('labels.driverModule.license.delete')"
                type="confirm"
                :content="$t('labels.driverModule.license.confirmDelete')"
                @hide="closePopupDelete"
                @confirm="() => actionDelete()" />
        </hts-drawer>
    </div>
</template>

<script lang="ts" setup>
import Actions from './actions.vue'
import EditLicense from './license-edit.vue'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
const { $formatters } = useNuxtApp()

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'screen-lg'
  },
  column: {
    type: Number,
    default: 3
  }
})

const idDriver = ref('')

const emits = defineEmits(['close', 'update:show', 'refreshTableDriver'])

const visible = ref(false)

const open = (idDriverSelected: string) => {
  visible.value = true
  server.value = {
    method: 'post',
    url: '/driver/license/list/' + idDriverSelected
  }
  idDriver.value = idDriverSelected
}
defineExpose({
  open
})
const editLicense = ref()
const addLicense = ref()
const showDelete = ref(false)
const dataLicense = ref()
const server = ref({})
const showEdit = ref(false)
const licenseSelected = ref({} as { [key: string]: any })
const refreshTableDriver = ref(false)

const openAddLicense = () => {
  if (addLicense.value) {
    addLicense.value.open(idDriver.value)
  }
  refreshTableDriver.value = false
}

const handleDelete = (license: any) => {
  showDelete.value = true
  licenseSelected.value = license
}

const handleEdit = (license: any) => {
  if (editLicense.value) {
    editLicense.value.open(license)
  }
}

const licenseDriver = [
  {
    field: 'licenseNumber',
    headerName: $t('labels.driverModule.license.no'),
    sortable: true
  },
  {
    field: 'licenseClass',
    headerName: $t('labels.driverModule.license.class'),
    sortable: true,
    valueGetter: (params: any) => {
      return params.data.licenseClass
    }
  },
  {
    field: 'expiredDate',
    headerName: $t('labels.driverModule.license.expires'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return params.data.expiredDate ? $formatters.date(params.data.expiredDate) : ''
    }
  },
  {
    field: 'licenseFront',
    headerName: $t('labels.driverModule.license.front'),
    maxWidth: 250,
    sortable: true,
    onCellClicked: (params: any) => {
      window.open(params.value)
    }
  },
  {
    field: 'licenseBack',
    headerName: $t('labels.driverModule.license.back'),
    maxWidth: 250,
    sortable: true,
    onCellClicked: (params: any) => {
      window.open(params.value)
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'driverDataTable',
      storeName: 'DriverLicense',
      items: [
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.driver_license_edit),
          click: handleEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-500',
          permissions: checkPermission(layoutPermissions.driver_license_delete),
          click: handleDelete,
          isArchive: true
        }
      ]
    }
  }
]

const refreshTables = (action?: string) => {
  if (dataLicense.value) {
    dataLicense.value.refresh()
  }
  if (action === 'add') {
    refreshTableDriver.value = true
  }
}

const closeLicense = () => {
  visible.value = false
  if (refreshTableDriver.value) {
    emits('refreshTableDriver')
  }
}

const closePopupDelete = () => {
  showDelete.value = false
  licenseSelected.value = {}
  refreshTableDriver.value = false
}

const actionDelete = () => {
  const api = $url.driver.license.delete

  $api(api, { body: { ids: [licenseSelected.value?._id], driver: idDriver.value } }).then((data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      Object.assign(licenseSelected, {})
      showDelete.value = false
      process.client && $toast().success($t('labels.deleteSuccess'))
      refreshTableDriver.value = true
      refreshTables()
    }
  })
}

const useArchive = { api: $url.driver.license.archive, permissions: checkPermission(layoutPermissions.driver_license_archive) }
const useRestore = { api: $url.driver.license.restore, permissions: checkPermission(layoutPermissions.driver_license_restore) }
</script>
fucntion/quote/status
